import {TAppDispatch, TRootState} from "types";
import {userEmailSelector} from "store/user/userSelectors";
import mapXCandidateApi from "api/candidateApi";
import {Mixpanel} from "helpers/mixpanel";
import {toast} from "react-toastify";
import {ToastContent} from "components";
import {
	clearRefreshLinkedInProfileScrapeInfo,
	refreshLinkedInCandidateInProgress,
	setCurrentRefreshLinkedInProfileScrapeInfo,
} from "store/refresh-candidate/refreshCandidateActions";
import {projectSelector} from "store/project-list/projectListSelectors";
import {selectedCandidateIdsForAssessmentSelector} from "store/project/Assessment/candidateScoringSelectors";
import {getCandidateInfoFromTLSelector} from "store/target-list/targetListSelectors";
import {SICandidate} from "api/candidateApi/types";
import {importLinkedInProfileToProject} from "store/bulk-import/bulkImportAsyncActions";
import {removeBulkRefreshDataForSource} from "store/bulk-import/bulkImportActions";
import {removeTrailingSlash} from "helpers/string";
import {settledImportResponseData} from "helpers/importErrorReason";
import {failedImportOutcome, fireImportFailedToaster} from "store/candidate/linkedInImportOutcome";
import {TUrlImportItem} from "mapx-components/BulkImport/types";
import {setImportLinkedInProfileInfoForCurrentImport} from "store/background-tasks/backgroundTaskActions";
import {STProject} from "api/projectApi/types";

export const refreshLinkedInProfile =
	(linkedInLink: string) => async (dispatch: TAppDispatch, getState: () => TRootState) => {
		const state = getState();

		const userEmail = userEmailSelector(state);

		const project = projectSelector(state);

		dispatch(refreshLinkedInCandidateInProgress({[linkedInLink]: true}));

		let response;

		try {
			const formBody = {linkedin_url: linkedInLink, project_id: project?.id ?? null};

			response = await mapXCandidateApi.createLinkedinUrlImportRequest(formBody);

			if (response.status === 201) {
				try {
					Mixpanel.track(`Refreshed LinkedIn Profile`, {
						pageTitle: window.location.href,
						url: linkedInLink,
						distinct_id: userEmail,
					});
					// eslint-disable-next-line
				} catch (e: any) {
					console.log(e.toString());
				}

				// Already failed when created (e.g. it could not be queued): no watcher will start for a
				// finished refresh, so it is finished here instead of staying "in progress".
				const failed = failedImportOutcome(response.data);

				if (failed) {
					fireImportFailedToaster("CandidateDetails Import", failed);
					dispatch(clearRefreshLinkedInProfileScrapeInfo(linkedInLink));

					return response.status;
				}

				dispatch(
					setCurrentRefreshLinkedInProfileScrapeInfo({
						[linkedInLink]: settledImportResponseData(response.data),
					}),
				);

				return response.status;
			} else {
				toast.error(ToastContent, {
					autoClose: false,
					closeOnClick: true,
					data: {title: response?.data?.detail || "Something went wrong!"},
				});

				dispatch(clearRefreshLinkedInProfileScrapeInfo(linkedInLink));

				return response.status;
			}
		} catch (error) {
			console.log(error);

			return 403;
		}
	};

export const bulkRefreshCandidateProfiles =
	() => async (dispatch: TAppDispatch, getState: () => TRootState) => {
		const state = getState();

		const project = projectSelector(state) as STProject;

		//get the candidate ids currently selected in redux state
		const selectedCandidateIdsForBulkRefresh = selectedCandidateIdsForAssessmentSelector(state);

		//get the candidate ids required bulk refresh from redux state
		const candidateInfoFromTL = getCandidateInfoFromTLSelector(
			state,
			selectedCandidateIdsForBulkRefresh,
		);

		//purify and prepare the array of candidate urls
		const urls = candidateInfoFromTL
			.filter((candidate: SICandidate) => candidate.linkedin_url !== null)
			.map((candidate: SICandidate) => ({
				url: removeTrailingSlash(candidate.linkedin_url ?? ""),
				status: "Created",
			}));

		if (urls?.length > 0) {
			const setImportStartPayload = {
				linkedin_candidate_urls: urls,
				progress: 0,
				project_id: project?.id,
				status: "In Progress",
				purpose: "refresh",
			};

			//set the data to state that enables the bulk import watcher
			dispatch(setImportLinkedInProfileInfoForCurrentImport(setImportStartPayload));

			// dispatch to create background task for current refresh request
			const created = await dispatch(
				importLinkedInProfileToProject(
					urls.map((item: TUrlImportItem) => item.url),
					project,
					"refresh",
				),
			);

			// No task, so nothing will ever finish this refresh: drop the entry set above instead of
			// leaving it "In Progress" beside the "could not be started" toast.
			if (!created) {
				dispatch(removeBulkRefreshDataForSource({project_id: project?.id, urls}));
			}

			const userEmail = userEmailSelector(state);

			Mixpanel.track(`Bulk Candidate Profile Refreshed`, {
				name: `${window.name}`,
				pageTitle: `${window.name}`,
				url: window.location.href,
				distinct_id: userEmail,
			});
		}
	};
